import { useMemo } from 'react';
import { buildCollectionTargets } from '../../../services/collectionTargets';
import { applyCollectionLead } from '../../../services/collectionLead';
import { alignCollectionTargets } from '../../../services/collectionPeriods';
import type { AlignedCollectionTargets } from '../../../services/collectionPeriods';
import { deriveEvenMonthlySchedule } from '../../../services/evenMonthlyTerm';
import type { EvenMonthlyDerivation } from '../../../services/evenMonthlyTerm';
import { todayDateKey } from '../../../utils/dateKey';
import type { SchedulingMethod } from '@/features/products/types/products.types';
import type { ConfiguredService, ConfiguredAddOn } from '@/features/service-plans/types/services.types';
import { EMPTY_ALIGNMENT, buildUnavailableReasons } from './derive';

export function useScheduleDerivation(options: {
    isItemBasedFamily: boolean;
    leadMonths: number;
    effectiveLeadMonths: number;
    minInstalments: number | undefined;
    defaultInstalments: number | undefined;
    selectedMethodKey: string | null;
    depositMinor: number | undefined;
    configuredServices: ConfiguredService[];
    configuredAddOns: ConfiguredAddOn[];
    feeGross: number;
    schedulingMethods: SchedulingMethod[] | undefined;
}): {
    itemBased: AlignedCollectionTargets;
    evenMonthly: EvenMonthlyDerivation;
    anchor: string;
    unavailableReasons: Readonly<Record<string, string>>;
} {
    const {
        isItemBasedFamily,
        leadMonths,
        effectiveLeadMonths,
        minInstalments,
        defaultInstalments,
        selectedMethodKey,
        depositMinor,
        configuredServices,
        configuredAddOns,
        feeGross,
        schedulingMethods,
    } = options;

    const { itemBased, evenMonthly, anchor } = useMemo(() => {
        const anchor = todayDateKey();
        return {
            anchor,
            itemBased: isItemBasedFamily
                ? alignCollectionTargets(
                      applyCollectionLead(
                          buildCollectionTargets(configuredServices, configuredAddOns, feeGross),
                          effectiveLeadMonths,
                      ),
                      anchor,
                  )
                : EMPTY_ALIGNMENT,
            evenMonthly: deriveEvenMonthlySchedule({
                services: configuredServices,
                addOns: configuredAddOns,
                anchorDateKey: anchor,
                leadMonths,
                minInstalments,
                defaultInstalments,
            }),
        };
        // Deliberately impure: one clock read per recompute shared by both families.
        // selectedMethodKey/depositMinor are unread anchor-refresh deps — removing either lets a stale anchor ride on a reshaped request.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        isItemBasedFamily,
        leadMonths,
        effectiveLeadMonths,
        minInstalments,
        defaultInstalments,
        selectedMethodKey,
        depositMinor,
        configuredServices,
        configuredAddOns,
        feeGross,
    ]);

    const unavailableReasons = useMemo(
        () => buildUnavailableReasons(schedulingMethods ?? [], evenMonthly),
        [schedulingMethods, evenMonthly],
    );

    return { itemBased, evenMonthly, anchor, unavailableReasons };
}
